import React, { useEffect, useState } from "react";
import { Country, State, City } from "country-state-city";
import axios from "axios";
import { API_URL } from "../../../utils/constant";
import Swal from "sweetalert2";

// Reusable component for inputs with show-on-bill checkbox
const LabeledInputWithCheckbox = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  show,
  className = "col-md-6 mb-3",
}) => (
  <div className={`${className}`}>
    <label className="form-label">{label}</label>
    <div className="input-group">
      {name === "footerStatement" ? (
        <textarea
          name={name}
          className="form-control"
          value={value}
          onChange={onChange}
          rows={3}
          placeholder="Enter footer statement for POS bill (paragraph supported)"
        />
      ) : (
        <input
          type={type}
          name={name}
          className="form-control"
          value={value}
          onChange={onChange}
          disabled={name === "email"}
          required={(name !== "website" && type === "text") || type === "email"}
        />
      )}
      <div className="input-group-text">
        <input
          type="checkbox"
          name={name}
          data-group="showOnBill"
          className="form-check-input mt-0"
          checked={show}
          onChange={onChange}
          id={`show-${name}`}
          title="Show in POS Bill"
        />
      </div>
    </div>
    <small className="text-muted">Show in POS Bill</small>
  </div>
);

const CompanySetup = () => {
  const SETUP_TYPES = [
    { key: "basic", name: "Basic", desc: "Company Name, Address and Statutory Info" },
    { key: "general", name: "General Setup", desc: "Currency, Location and Accounting Period Preferences" },
    { key: "sales", name: "Sales Setup", desc: "Sales Billing Configuration and Preferences" },
    { key: "purchase", name: "Purchase Setup", desc: "Purchase Billing Configuration and Preferences" },
    { key: "inventory", name: "Inventory", desc: "Inventory and Item Preferences Configuration" },
  ];

  const [activeTab, setActiveTab] = useState("basic");

  const [invoiceSerial, setInvoiceSerial] = useState({
    product: { prefix: "", start: "", suffix: "session", suffixText: "" },
    service: { prefix: "", start: "", suffix: "session", suffixText: "" },
    nonGst: { prefix: "", start: "", suffix: "session", suffixText: "" }
  });
  const company = JSON.parse(localStorage.getItem("company")) || {};

  const [formData, setFormData] = useState({
    companyName: "",
    tradeName: "",
    incorporationDate: "",
    companyType: "",
    contact: "",
    email: "",
    country: "",
    state: "",
    city: "",
    address: "",
    pincode: "",
    gstNo: "",
    website: "",
    pan: "",
    posLogo: null,
    company_logo: null,
    footerStatement: "",
    showOnBill: {
      companyName: true,
      tradeName: true,
      gstNo: true,
      address: true,
      contact: true,
      website: false,
      pan: false,
      footerStatement: true,
    },
  });

  const [generalData, setGeneralData] = useState({
    businessType: "",
    taxApplicable: "",
    cessApplicable: "",
    baseCurrency: "INR",
    currencySymbolPosition: "before",
    decimalPlaces: "2",
    timeZone: "Asia/Kolkata",
    financialYearStart: "April",
    financialYearEnd: "March",
    accountingMethod: "Accrual",
    dateFormat: "DD-MM-YYYY",
    numberFormat: "indian",
    enableMultiCurrency: false,
    ageingDaysCustomer: [30, 60, 90, 120, 150, 0],
    ageingDaysVendor: [30, 60, 90, 120, 150, 0],
    ageingDaysStock: [30, 60, 90, 120, 150, 0],
  });

  const [salesData, setSalesData] = useState({
    customerLabel: "Customer",
    salesmanWiseBilling: "",
    taxInRates: "Exclusive",
    postingLedgerBasedOn: "item",
    showPurchasePrice: false,
  });

  const [purchaseData, setPurchaseData] = useState({
    vendorLabel: "Vendor",
    taxInRates: "Exclusive",
    postingLedgerBasedOn: "item",
    showPurchasePrice: false,
  });

  const [inventoryData, setInventoryData] = useState({
    itemStockUnit: "",
    warehouseOption: "",
    barcodeScanBasedOn: "barcode",
    stockValuationMethod: "FIFO",
    stockValuationLevel: "location_warehouse",

    barcodeScanLookup: true,
    minimumStockLevels: true,
  });



  const [companyData, setCompanyData] = useState({});
  const [pendingState, setPendingState] = useState(null);
  const [pendingCity, setPendingCity] = useState(null);
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [posImg, setPosImg] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);

  // Country list on mount
  useEffect(() => {
    setCountryList(Country.getAllCountries());
  }, []);

  // Fetch company info
  const fetchCompany = async () => {
    if (!company.company?.id) return;
    try {
      const res = await axios.get(`${API_URL}/companies/${company.company?.id}`);
      const data = res.data;
      setCompanyData(data);
      setFormData((prev) => ({
        ...prev,
        companyName: data.company_name || prev.companyName,
        tradeName: data.trade || prev.tradeName,
        incorporationDate: data.doi || prev.incorporationDate,
        companyType: data.company_type || prev.companyType,
        contact: data.mobile_no || prev.contact,
        email: data.email || prev.email,
        country: data.country,
        state: data.state,
        city: data.city,
        address: data.register_address || prev.address,
        pincode: data.pincode || prev.pincode,
        gstNo: data.gst_number || prev.gstNo,
        website: data.website || prev.website,
        pan: data.company_pan_number || prev.pan,
        footerStatement: data.footer_statement || prev.footerStatement,
        showOnBill: JSON.parse(data.showOnBill) || prev.showOnBill,
      }));
      setPosImg(data.posLogo);
      setCompanyLogo(data.company_logo);
    } catch (err) {
      console.error("Failed to fetch company:", err);
    }
  };

  const fetchCompanyOtherData = async () => {
    if (!company.company?.id) return;
    try {
      const res = await axios.get(`${API_URL}/companies/company/other-data/${company.company?.id}`);
      const data = res.data?.companyOtherDetail;

       if (!data) return;

    // ✅ General Data
    setGeneralData(prev => ({
      ...prev,
      businessType: data.businessType || "",
      taxApplicable: data.taxApplicable || "",
      cessApplicable: data.cessApplicable || "",
      baseCurrency: data.baseCurrency || "INR",
      currencySymbolPosition: data.currencySymbolPosition || "before",
      decimalPlaces: data.decimalPlaces || "2",
      timeZone: data.timeZone || "Asia/Kolkata",
      financialYearStart: data.financialYearStart || "April",
      financialYearEnd: data.financialYearEnd || "March",
      accountingMethod: data.accountingMethod || "Accrual",
      dateFormat: data.dateFormat || "DD-MM-YYYY",
      numberFormat: data.numberFormat || "indian",
      enableMultiCurrency: data.enableMultiCurrency ?? false,
      ageingDaysCustomer: data.ageingDaysCustomer || [30, 60, 90, 120, 150, 0],
      ageingDaysVendor: data.ageingDaysVendor || [30, 60, 90, 120, 150, 0],
      ageingDaysStock: data.ageingDaysStock || [30, 60, 90, 120, 150, 0],
    }));

    // ✅ Sales Data
    setSalesData(prev => ({
      ...prev,
      customerLabel: data.customerLabel || "Customer",
      salesmanWiseBilling: data.salesmanWiseBilling || "",
      taxInRates: data.salesTaxInRates || "Exclusive",
      postingLedgerBasedOn: data.salesPostingLedgerBasedOn || "item",
      showPurchasePrice: data.salesShowPurchasePrice ?? false,
    }));

    // ✅ Purchase Data
    setPurchaseData(prev => ({
      ...prev,
      vendorLabel: data.vendorLabel || "Vendor",
      taxInRates: data.purchaseTaxInRates || "Exclusive",
      postingLedgerBasedOn: data.purchasePostingLedgerBasedOn || "item",
      showPurchasePrice: data.purchaseShowPurchasePrice ?? false,
    }));

    // ✅ Inventory Data
    setInventoryData(prev => ({
      ...prev,
      itemStockUnit: data.itemStockUnit || "",
      warehouseOption: data.warehouseOption || "",
      barcodeScanBasedOn: data.barcodeScanBasedOn || "barcode",
      stockValuationMethod: data.stockValuationMethod || "FIFO",
      stockValuationLevel: data.stockValuationLevel || "location_warehouse",
      barcodeScanLookup: data.barcodeScanLookup ?? true,
      minimumStockLevels: data.minimumStockLevels ?? true,
    }));

    console.log(data.product_invoice );
    // ✅ Invoice Serial
    setInvoiceSerial({
      product: {
        prefix: data.product_invoice.prefix || "",
        start: data.product_invoice.start || "",
        suffix: data.product_invoice.suffix || "session",
        suffixText: data.product_invoice.suffixText || "",
      },
      service: {
        prefix: data.service_invoice.prefix || "",
        start: data.service_invoice.start || "",
        suffix: data.service_invoice.suffix || "session",
        suffixText: data.service_invoice.suffixText || "",
      },
      nonGst: {
        prefix: data.non_gst_invoice.prefix || "",
        start: data.non_gst_invoice.start || "",
        suffix: data.non_gst_invoice.suffix || "session",
        suffixText: data.non_gst_invoice.suffixText || "",
      },
    });

    } catch (error) {
      console.error("Failed to fetch company:", error);
    }
  }

  useEffect(() => {
    fetchCompany();
    fetchCompanyOtherData();
  }, [company.company?.id]);

  // Populate state list when country changes
  useEffect(() => {
    if (formData.country) {
      const selectedCountry = countryList.find((c) => c.name === formData.country);
      setStateList(selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode) : []);
      setFormData((prev) => ({ ...prev, state: "", city: "" }));
      setPendingState(companyData.state);
      setPendingCity(companyData.city);
    }
  }, [formData.country, countryList, companyData.state, companyData.city]);

  // Populate city list when state changes
  useEffect(() => {
    if (formData.state && formData.country) {
      const selectedCountry = countryList.find((c) => c.name === formData.country);
      const selectedState = stateList.find((s) => s.name === formData.state);
      setCityList(
        selectedCountry && selectedState
          ? City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode)
          : []
      );
      setFormData((prev) => ({ ...prev, city: "" }));
    }
  }, [formData.state, formData.country, stateList, countryList]);

  // Set pending state/city after lists are loaded
  useEffect(() => {
    if (pendingState && stateList.length > 0) {
      const found = stateList.find((s) => s.name === pendingState);
      if (found) {
        setFormData((prev) => ({ ...prev, state: pendingState }));
        setPendingState(null);
      }
    }
  }, [pendingState, stateList]);

  useEffect(() => {
    if (pendingCity && cityList.length > 0) {
      const found = cityList.find((c) => c.name === pendingCity);
      if (found) {
        setFormData((prev) => ({ ...prev, city: pendingCity }));
        setPendingCity(null);
      }
    }
  }, [pendingCity, cityList]);

  const handleChange = (e) => {
    const { name, value, type, checked, files, dataset } = e.target;

    if (dataset.group === "showOnBill") {
      setFormData({
        ...formData,
        showOnBill: { ...formData.showOnBill, [name]: checked },
      });
    } else if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else if (type === "file") {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   if (!company.company?.id) return;

  //   const payload = new FormData();

  //   // Append all fields except showOnBill
  //   Object.keys(formData).forEach((key) => {
  //     if (key !== "showOnBill") payload.append(key, formData[key]);
  //   });

  //   payload.append("showOnBill", JSON.stringify(formData.showOnBill));

  //   Object.keys(generalData).forEach((key) => {
  //     payload.append(key, generalData[key]);
  //   });

  //   // Sales tab
  //   Object.keys(salesData).forEach((key) => {
  //     payload.append(key, salesData[key]);
  //   });

  //   // Purchase tab
  //   Object.keys(purchaseData).forEach((key) => {
  //     payload.append(key, purchaseData[key]);
  //   });

  //   // Inventory tab
  //   Object.keys(inventoryData).forEach((key) => {
  //     payload.append(key, inventoryData[key]);
  //   });

  //   try {
  //     const res = await axios.put(
  //       `${API_URL}/companies/${company.company?.id}`,
  //       payload,
  //       { headers: { "Content-Type": "multipart/form-data" } }
  //     );

  //     // Success toast
  //     Swal.fire({
  //       toast: true,
  //       position: "top-end",
  //       icon: "success",
  //       title: "Company profile submitted successfully!",
  //       showConfirmButton: false,
  //       timer: 3000,
  //       timerProgressBar: true,
  //     });

  //     // Optionally refresh company data
  //     fetchCompany();
  //   } catch (err) {
  //     console.error("Submission failed:", err);

  //     // Error toast
  //     Swal.fire({
  //       toast: true,
  //       position: "top-end",
  //       icon: "error",
  //       title: err.response?.data?.message || "Error submitting company profile",
  //       showConfirmButton: false,
  //       timer: 4000,
  //       timerProgressBar: true,
  //     });
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!company.company?.id) return;

    const payload = new FormData();

    // Append main company form fields except showOnBill
    Object.keys(formData).forEach((key) => {
      if (key !== "showOnBill") payload.append(key, formData[key]);
    });

    // Append showOnBill as JSON string
    payload.append("showOnBill", JSON.stringify(formData.showOnBill));

    // Append generalData as JSON string
    payload.append("generalData", JSON.stringify(generalData));

    // Append salesData as JSON string
    payload.append("salesData", JSON.stringify(salesData));

    // Append purchaseData as JSON string
    payload.append("purchaseData", JSON.stringify(purchaseData));


    const finalInventoryData = {
      ...inventoryData,
      product_invoice: invoiceSerial.product,
      service_invoice: invoiceSerial.service,
      non_gst_invoice: invoiceSerial.nonGst
    };

      // Append inventoryData as JSON string
      payload.append("inventoryData", JSON.stringify(finalInventoryData));


    try {
      const res = await axios.put(
        `${API_URL}/companies/${company.company?.id}`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      // Success toast
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Company profile submitted successfully!",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      // Refresh company data
      fetchCompany();
    } catch (err) {
      console.error("Submission failed:", err);

      // Error toast
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: err.response?.data?.message || "Error submitting company profile",
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      });
    }
  };


  const handleSerialChange = (type, field, value) => {
    setInvoiceSerial((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
  };

  const renderBasicTab = () => (
    <>
      <h5 className="mb-4">Basic Company Details</h5>
      <div className="row">
        {companyLogo && (
          <div className="col-md-2">
            <img
              src={`${API_URL}${companyLogo}`}
              alt="Company Logo"
              className="img-thumbnail mb-3"
              style={{ maxWidth: "100%" }}
            />
          </div>
        )}
        <div className={`${companyLogo ? "col-md-4" : "col-md-6"} mb-3`}>
          <label>Company Logo</label>
          <input
            type="file"
            name="company_logo"
            className="form-control"
            onChange={handleChange}
            accept="image/*"
          />
        </div>

        {/* Fields with show-on-bill */}
        <LabeledInputWithCheckbox
          label="Company Name"
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          show={formData.showOnBill.companyName}
        />
        <LabeledInputWithCheckbox
          label="Trade Name"
          name="tradeName"
          value={formData.tradeName}
          onChange={handleChange}
          show={formData.showOnBill.tradeName}
        />
        <LabeledInputWithCheckbox
          label="GST No"
          name="gstNo"
          value={formData.gstNo}
          onChange={handleChange}
          show={formData.showOnBill.gstNo}
        />
        <LabeledInputWithCheckbox
          label="Address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          show={formData.showOnBill.address}
        />
        <LabeledInputWithCheckbox
          label="Contact"
          name="contact"
          value={formData.contact}
          onChange={handleChange}
          show={formData.showOnBill.contact}
        />
        <LabeledInputWithCheckbox
          label="Website Link"
          name="website"
          value={formData.website}
          onChange={handleChange}
          show={formData.showOnBill.website}
        />
        <LabeledInputWithCheckbox
          label="PAN"
          name="pan"
          value={formData.pan}
          onChange={handleChange}
          show={formData.showOnBill.pan}
        />
        <LabeledInputWithCheckbox
          label="POS Footer Statement"
          name="footerStatement"
          value={formData.footerStatement}
          onChange={handleChange}
          show={formData.showOnBill.footerStatement}
          className="col-md-12 mb-3 h-30"
        />

        <div className="col-md-6 mb-3">
          <label>Incorporation Date</label>
          <input
            type="date"
            name="incorporationDate"
            className="form-control"
            value={formData.incorporationDate}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label>Company Type</label>
          <input
            type="text"
            name="companyType"
            className="form-control"
            value={formData.companyType}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label>Email</label>
          <input
            type="email"
            name="email"
            className="form-control"
            value={formData.email}
            readOnly
            disabled
          />
        </div>

        <div className="col-md-6 mb-3">
          <label>Country</label>
          <select
            name="country"
            className="form-control"
            value={formData.country}
            onChange={handleChange}
          >
            <option value="">Select Country</option>
            {countryList.map((c) => (
              <option key={c.isoCode} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6 mb-3">
          <label>State</label>
          <select
            name="state"
            className="form-control"
            value={formData.state}
            onChange={handleChange}
            disabled={!formData.country}
          >
            <option value="">Select State</option>
            {stateList.map((s) => (
              <option key={s.isoCode} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6 mb-3">
          <label>City</label>
          <select
            name="city"
            className="form-control"
            value={formData.city}
            onChange={handleChange}
            disabled={!formData.state}
          >
            <option value="">Select City</option>
            {cityList.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6 mb-3">
          <label>Pincode</label>
          <input
            type="text"
            name="pincode"
            className="form-control"
            value={formData.pincode}
            onChange={handleChange}
          />
        </div>

        {posImg && (
          <div className="col-md-2">
            <img
              src={`${API_URL}${posImg}`}
              alt="POS Logo"
              className="img-thumbnail mb-3"
              style={{ maxWidth: "100%" }}
            />
          </div>
        )}

        <div className={`${posImg ? "col-md-4" : "col-md-6"} mb-3`}>
          <label>Logo for POS Billing</label>
          <input
            type="file"
            name="posLogo"
            className="form-control"
            onChange={handleChange}
            accept="image/*"
          />
        </div>
      </div>
    </>
  );

  // You can reuse your original renderForm logic for general/sales/purchase/inventory
  const renderForm = () => {
    switch (activeTab) {
      case "basic":
        return renderBasicTab();
      case "general":
        return (
          <>
            <h5 className="mb-4">General Setup</h5>

            {/* Basic Preferences */}
            <h6 className="mb-3">Basic Preferences</h6>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Business Type</label>
                <select
                  className="form-select"
                  value={generalData.businessType}
                  onChange={(e) =>
                    setGeneralData({ ...generalData, businessType: e.target.value })
                  }
                >
                  <option value="">None</option>
                  <option value="books_stationary">Books Stationary</option>
                  <option value="electronics">Electronics</option>
                  <option value="garment_store">Garment Store</option>
                  <option value="food_restaurant">Food Restaurant</option>
                  <option value="hotels">Hotels</option>
                  <option value="jobwork_repair">Jobwork Repair</option>
                  <option value="mobile_store">Mobile Store</option>
                  <option value="pharmacy">Pharmacy</option>
                  <option value="professional_services">Professional Services</option>
                  <option value="security_services">Security Services</option>
                  <option value="tour_travel">Tour Travel</option>
                  <option value="trading_business">Trading Business</option>
                  <option value="grocery_store">Grocery Store</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="retail_counter">Retail Counter</option>
                  <option value="retail_chain_store">Retail Chain Store</option>
                  <option value="automobile">Automobile</option>
                  <option value="jewellery">Jewellery</option>
                  <option value="optician">Optician</option>
                  <option value="contracting_projects">Contracting Projects</option>
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Tax Applicable in Sales Purchase</label>
                <select
                  className="form-select"
                  value={generalData.taxApplicable}
                  onChange={(e) =>
                    setGeneralData({ ...generalData, taxApplicable: e.target.value })
                  }
                >
                  <option value="">Not Applicable</option>
                  <option value="GST">GST</option>
                  <option value="VAT">VAT</option>
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Cess Applicable</label>
                <select
                  className="form-select"
                  value={generalData.cessApplicable}
                  onChange={(e) =>
                    setGeneralData({ ...generalData, cessApplicable: e.target.value })
                  }
                >
                  <option value="central">Central</option>
                  <option value="state">State</option>
                  <option value="both">Both</option>
                  <option value="none">None</option>
                </select>
              </div>
            </div>

            {/* Currency Settings */}
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Base Currency</label>
                <select
                  className="form-select"
                  value={generalData.baseCurrency}
                  onChange={(e) =>
                    setGeneralData({ ...generalData, baseCurrency: e.target.value })
                  }
                >
                  <option value="INR">INR - Indian Rupee</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Currency Symbol Position</label>
                <select
                  className="form-select"
                  value={generalData.currencySymbolPosition}
                  onChange={(e) =>
                    setGeneralData({
                      ...generalData,
                      currencySymbolPosition: e.target.value,
                    })
                  }
                >
                  <option value="before">Before Amount (₹100)</option>
                  <option value="after">After Amount (100₹)</option>
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Decimal Places</label>
                <select
                  className="form-select"
                  value={generalData.decimalPlaces}
                  onChange={(e) =>
                    setGeneralData({ ...generalData, decimalPlaces: e.target.value })
                  }
                >
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="0">0</option>
                </select>
              </div>
            </div>

            {/* Country & Timezone */}
            <div className="row">

              <div className="col-md-6 mb-3">
                <label className="form-label">Time Zone</label>
                <select
                  className="form-select"
                  value={generalData.timeZone}
                  onChange={(e) =>
                    setGeneralData({ ...generalData, timeZone: e.target.value })
                  }
                >
                  <option value="Asia/Kolkata">Asia/Kolkata</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
            </div>

            <hr />

            {/* Accounting Period */}
            <h6 className="mb-3">Accounting Period</h6>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Financial Year Start</label>
                <select
                  className="form-select"
                  value={generalData.financialYearStart}
                  onChange={(e) =>
                    setGeneralData({
                      ...generalData,
                      financialYearStart: e.target.value,
                    })
                  }
                >
                  <option value="April">April</option>
                  <option value="January">January</option>
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Financial Year End</label>
                <select
                  className="form-select"
                  value={generalData.financialYearEnd}
                  onChange={(e) =>
                    setGeneralData({ ...generalData, financialYearEnd: e.target.value })
                  }
                >
                  <option value="March">March</option>
                  <option value="December">December</option>
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Accounting Method</label>
                <select
                  className="form-select"
                  value={generalData.accountingMethod}
                  onChange={(e) =>
                    setGeneralData({ ...generalData, accountingMethod: e.target.value })
                  }
                >
                  <option value="Accrual">Accrual</option>
                  <option value="Cash">Cash</option>
                </select>
              </div>
            </div>

            <hr />

            {/* Other Preferences */}
            <h6 className="mb-3">Other Preferences</h6>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Date Format</label>
                <select
                  className="form-select"
                  value={generalData.dateFormat}
                  onChange={(e) =>
                    setGeneralData({ ...generalData, dateFormat: e.target.value })
                  }
                >
                  <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                  <option value="MM-DD-YYYY">MM-DD-YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Number Format</label>
                <select
                  className="form-select"
                  value={generalData.numberFormat}
                  onChange={(e) =>
                    setGeneralData({ ...generalData, numberFormat: e.target.value })
                  }
                >
                  <option value="indian">1,23,456.00 (Indian)</option>
                  <option value="international">123,456.00 (International)</option>
                </select>
              </div>

              <div className="col-md-4 mb-3 d-flex align-items-center">
                <div className="form-check mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={generalData.enableMultiCurrency}
                    onChange={(e) =>
                      setGeneralData({
                        ...generalData,
                        enableMultiCurrency: e.target.checked,
                      })
                    }
                  />
                  <label className="form-check-label">Enable Multi Currency</label>
                </div>
              </div>
            </div>

            <hr />

            {/* Ageing Days */}
            <h6 className="mb-3">Ageing Days</h6>
            {["Customer", "Vendor", "Stock"].map((type) => (
              <div className="col-md-12 mb-3" key={type}>
                <label className="form-label">{type}</label>
                <div className="d-flex gap-2">
                  {generalData[`ageingDays${type}`].map((val, idx) => (
                    <input
                      key={idx}
                      type="number"
                      className="form-control"
                      value={val}
                      onChange={(e) => {
                        const newArr = [...generalData[`ageingDays${type}`]];
                        newArr[idx] = e.target.value;
                        setGeneralData({
                          ...generalData,
                          [`ageingDays${type}`]: newArr,
                        });
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </>
        );


      case "sales":
        return (
          <>
            <h5 className="mb-4">Sales Setup</h5>

            <div className="row">
              {/* Customer Label */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Customer Label</label>
                <select
                  className="form-select"
                  value={salesData.customerLabel}
                  onChange={(e) =>
                    setSalesData({ ...salesData, customerLabel: e.target.value })
                  }
                >
                  <option value="Customer">Customer</option>
                  <option value="Client">Client</option>
                  <option value="Tenant">Tenant</option>
                  <option value="Patient">Patient</option>
                  <option value="Donor">Donor</option>
                  <option value="Member">Member</option>
                  <option value="Student">Student</option>
                </select>
              </div>

              {/* Salesman Wise Billing */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Salesman Wise Billing</label>
                <select
                  className="form-select"
                  value={salesData.salesmanId}
                  onChange={(e) =>
                    setSalesData({ ...salesData, salesmanId: e.target.value })
                  }
                >
                  <option value="">Select Salesman</option>
                  <option value="1">John Doe</option>
                  <option value="2">Jane Smith</option>
                </select>
              </div>

              {/* Tax in Rates */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Tax in Rates</label>
                <select
                  className="form-select"
                  value={salesData.taxInRates}
                  onChange={(e) =>
                    setSalesData({ ...salesData, taxInRates: e.target.value })
                  }
                >
                  <option value="Inclusive">Inclusive</option>
                  <option value="Exclusive">Exclusive</option>
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Posting Ledger Based On</label>
                <select
                  className="form-select"
                  value={salesData.postingLedgerBasedOn}
                  onChange={(e) =>
                    setSalesData({ ...salesData, postingLedgerBasedOn: e.target.value })
                  }
                >
                  <option value="item">Item</option>
                  <option value="tax">Tax</option>
                </select>
              </div>
            </div>

            {/* Switch */}
            <div className="mb-3 form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={salesData.showPurchasePrice}
                onChange={(e) =>
                  setSalesData({ ...salesData, showPurchasePrice: e.target.checked })
                }
              />
              <label className="form-check-label">Show Purchase Price at Billing</label>
            </div>
          </>
        );

      case "purchase":
        return (
          <>
            <h5 className="mb-4">Purchase Setup</h5>

            <div className="row">
              {/* Vendor Label */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Vendor Label</label>
                <select
                  className="form-select"
                  value={purchaseData.vendorLabel}
                  onChange={(e) =>
                    setPurchaseData({ ...purchaseData, vendorLabel: e.target.value })
                  }
                >
                  <option value="Vendor">Vendor</option>
                  <option value="Supplier">Supplier</option>
                  <option value="Jobber">Jobber</option>
                  <option value="Contractor">Contractor</option>
                  <option value="Merchant">Merchant</option>
                </select>
              </div>

              {/* Tax in Rates */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Tax in Rates</label>
                <select
                  className="form-select"
                  value={purchaseData.taxInRates}
                  onChange={(e) =>
                    setPurchaseData({ ...purchaseData, taxInRates: e.target.value })
                  }
                >
                  <option value="Inclusive">Inclusive</option>
                  <option value="Exclusive">Exclusive</option>
                </select>
              </div>

              {/* Posting Ledger */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Posting Ledger Based On</label>
                <select
                  className="form-select"
                  value={purchaseData.postingLedgerBasedOn}
                  onChange={(e) =>
                    setPurchaseData({ ...purchaseData, postingLedgerBasedOn: e.target.value })
                  }
                >
                  <option value="item">Item</option>
                  <option value="tax">Tax</option>
                </select>
              </div>
            </div>

            {/* Switch */}
            <div className="mb-3 form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={purchaseData.showPurchasePrice}
                onChange={(e) =>
                  setPurchaseData({ ...purchaseData, showPurchasePrice: e.target.checked })
                }
              />
              <label className="form-check-label">Show Purchase Price at Billing</label>
            </div>
          </>
        );

      case "inventory":
        return (
          <>
            <h5 className="mb-4">Inventory Setup</h5>
            <div className="row">
              {/* Item Stock Unit */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Item Stock Unit option</label>
                <select
                  className="form-select"
                  value={inventoryData.itemStockUnit}
                  onChange={(e) =>
                    setInventoryData({ ...inventoryData, itemStockUnit: e.target.value })
                  }
                >
                  <option value="">Select...</option>
                  <option value="Stock Unit">Stock Unit</option>
                  <option value="Stock Pack Unit">Stock Pack Unit</option>
                  <option value="Stock Pack Gpack Unit">Stock Pack Gpack Unit</option>
                </select>
              </div>

              {/* Warehouse Option */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Warehouse Option</label>
                <select
                  className="form-select"
                  value={inventoryData.warehouseOption}
                  onChange={(e) =>
                    setInventoryData({ ...inventoryData, warehouseOption: e.target.value })
                  }
                >
                  <option value="">Not Required</option>
                  <option value="document_level">Document Level</option>
                  <option value="line_level">Line Level</option>
                </select>
              </div>

              {/* Barcode scan based on */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Barcode scan based on</label>
                <select
                  className="form-select"
                  value={inventoryData.barcodeBasedOn}
                  onChange={(e) =>
                    setInventoryData({ ...inventoryData, barcodeBasedOn: e.target.value })
                  }
                >
                  <option value="item_code">Item Code</option>
                  <option value="serial_no">Serial Number</option>
                  <option value="barcode">Barcode</option>
                  <option value="batch_no">Batch Number</option>
                  <option value="jewellery_tag_no">Jewellery Tag Number</option>
                </select>
              </div>

              {/* Stock Valuation */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Stock Valuation Method</label>
                <select
                  className="form-select"
                  value={inventoryData.stockValuationMethod}
                  onChange={(e) =>
                    setInventoryData({ ...inventoryData, stockValuationMethod: e.target.value })
                  }
                >
                  <option value="FIFO">FIFO</option>
                  <option value="LIFO">LIFO</option>
                  <option value="weighted_avg">Weighted Average</option>
                  <option value="weighted_avg_val">Weighted Average with Value</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Stock Valuation Level</label>
                <select
                  className="form-select"
                  value={inventoryData.stockValuationLevel}
                  onChange={(e) =>
                    setInventoryData({ ...inventoryData, stockValuationLevel: e.target.value })
                  }
                >
                  <option value="location_warehouse">Location Warehouse</option>
                  <option value="location">Location</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="overall">Overall</option>
                </select>
              </div>


              <h5 className="mb-4 mt-4">Billing Invoice Format</h5>

              {/* PRODUCT INVOICE */}
              <div className="row mb-4">
                <div className="col-md-12 mb-3">
                  <label className="form-label fw-bold">Product Invoice Serial</label>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Prefix</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="PRD"
                    value={invoiceSerial.product.prefix}
                    onChange={(e) =>
                      handleSerialChange("product", "prefix", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Starting Serial Number</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="1001"
                    value={invoiceSerial.product.start}
                    onChange={(e) =>
                      handleSerialChange("product", "start", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Suffix Type</label>
                  <select
                    className="form-control"
                    value={invoiceSerial.product.suffix}
                    onChange={(e) =>
                      handleSerialChange("product", "suffix", e.target.value)
                    }
                  >
                    <option value="session">Session</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                {invoiceSerial.product.suffix === "custom" && (
                  <div className="col-md-3">
                    <label className="form-label">Custom Suffix</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter custom suffix"
                      value={invoiceSerial.product.suffixText}
                      onChange={(e) =>
                        handleSerialChange("product", "suffixText", e.target.value)
                      }
                    />
                  </div>
                )}
              </div>

              {/* SERVICE INVOICE */}
              <div className="row mb-4">
                <div className="col-md-12 mb-3">
                  <label className="form-label fw-bold">Service Invoice Serial</label>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Prefix</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="SRV"
                    value={invoiceSerial.service.prefix}
                    onChange={(e) =>
                      handleSerialChange("service", "prefix", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Starting Serial Number</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="1001"
                    value={invoiceSerial.service.start}
                    onChange={(e) =>
                      handleSerialChange("service", "start", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Suffix Type</label>
                  <select
                    className="form-control"
                    value={invoiceSerial.service.suffix}
                    onChange={(e) =>
                      handleSerialChange("service", "suffix", e.target.value)
                    }
                  >
                    <option value="session">Session</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                {invoiceSerial.service.suffix === "custom" && (
                  <div className="col-md-3">
                    <label className="form-label">Custom Suffix</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter custom suffix"
                      value={invoiceSerial.service.suffixText}
                      onChange={(e) =>
                        handleSerialChange("service", "suffixText", e.target.value)
                      }
                    />
                  </div>
                )}
              </div>

              {/* NON GST INVOICE */}
              <div className="row mb-4">
                <div className="col-md-12 mb-3">
                  <label className="form-label fw-bold">Non GST Invoice Serial</label>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Prefix</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="NGST"
                    value={invoiceSerial.nonGst.prefix}
                    onChange={(e) =>
                      handleSerialChange("nonGst", "prefix", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Starting Serial Number</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="1001"
                    value={invoiceSerial.nonGst.start}
                    onChange={(e) =>
                      handleSerialChange("nonGst", "start", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Suffix Type</label>
                  <select
                    className="form-control"
                    value={invoiceSerial.nonGst.suffix}
                    onChange={(e) =>
                      handleSerialChange("nonGst", "suffix", e.target.value)
                    }
                  >
                    <option value="session">Session</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                {invoiceSerial.nonGst.suffix === "custom" && (
                  <div className="col-md-3">
                    <label className="form-label">Custom Suffix</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter custom suffix"
                      value={invoiceSerial.nonGst.suffixText}
                      onChange={(e) =>
                        handleSerialChange("nonGst", "suffixText", e.target.value)
                      }
                    />
                  </div>
                )}
              </div>
              {/* Switches */}
              {/* <div className="mb-3">
                <label className="form-label">Barcode scan-lookup</label>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={inventoryData.barcodeLookup}
                    onChange={(e) =>
                      setInventoryData({ ...inventoryData, barcodeLookup: e.target.checked })
                    }
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Minimum stock levels</label>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={inventoryData.minimumStockLevels}
                    onChange={(e) =>
                      setInventoryData({ ...inventoryData, minimumStockLevels: e.target.checked })
                    }
                  />
                </div>
              </div> */}
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container-fluid">
      <div className="row min-vh-100">
        {/* LEFT SIDEBAR */}
        <div className="col-md-4 col-lg-3 p-3">
          <div className="nav flex-column nav-pills tab-style-6">
            {SETUP_TYPES.map((item) => (
              <button
                key={item.key}
                className={`nav-link text-start d-grid mb-2 ${activeTab === item.key ? "active" : ""
                  }`}
                onClick={() => setActiveTab(item.key)}
              >
                <h5
                  className={`fw-semibold mb-2 ${activeTab === item.key ? "text-white" : "text-primary"
                    }`}
                >
                  {item.name}
                </h5>
                <small className="text-muted">{item.desc}</small>
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT CONTENT */}
        <div className="col-md-8 col-lg-9 p-3">
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit} encType="multipart/form-data">
                {renderForm()}

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button type="button" className="btn btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySetup;
